import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { Experience, ExperienceCheckDomMutation, ExperienceCheckTimeout, containsPopupWithNestedElement, getPopupContainerFromEditorView } from '@atlaskit/editor-common/experiences';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
var pluginKey = new PluginKey('selectionToolbarOpenExperience');
var START_METHOD = {
  MOUSE_UP: 'mouse-up',
  KEY_DOWN: 'key-down'
};
var ABORT_REASON = {
  SELECTION_CLEARED: 'selection-cleared',
  BLOCK_MENU_OPENED: 'block-menu-opened',
  EDITOR_DESTROYED: 'editor-destroyed'
};
/**
 * This experience tracks when the selection toolbar is opened.
 *
 * Start: When user makes a selection via mouseup or shift+arrow key down
 * Success: When the selection toolbar is added to the DOM within 1000ms of start
 * Failure: When 1000ms passes without the selection toolbar being added to the DOM
 * Abort: When selection transitions to empty or block menu is opened
 */
export var getSelectionToolbarOpenExperiencePlugin = function getSelectionToolbarOpenExperiencePlugin(_ref) {
  var refs = _ref.refs,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent;
  var editorView;
  var targetEl;
  var shiftArrowKeyPressed = false;
  var mouseDownPos;
  var getTarget = function getTarget() {
    if (!targetEl) {
      var _editorView;
      targetEl = refs.popupsMountPoint || getPopupContainerFromEditorView((_editorView = editorView) === null || _editorView === void 0 ? void 0 : _editorView.dom);
    }
    return targetEl;
  };
  var experience = new Experience('toolbar-open', {
    actionSubjectId: 'selection-toolbar',
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    checks: [new ExperienceCheckTimeout({
      durationMs: 1000,
      onTimeout: function onTimeout() {
        var _editorView2;
        if (isBlockMenuWithinNode(getTarget())) {
          return {
            status: 'abort',
            reason: ABORT_REASON.BLOCK_MENU_OPENED
          };
        } else if (isSelectionWithoutTextContent((_editorView2 = editorView) === null || _editorView2 === void 0 ? void 0 : _editorView2.state.selection)) {
          return {
            status: 'abort',
            reason: ABORT_REASON.SELECTION_CLEARED
          };
        }
      }
    }), new ExperienceCheckDomMutation({
      onDomMutation: function onDomMutation(_ref2) {
        var mutations = _ref2.mutations;
        // @ts-ignore - Workaround for help-center local consumption

        if (mutations.some(isSelectionToolbarAddedInMutation)) {
          return {
            status: 'success'
          };
        }
      },
      observeConfig: function observeConfig() {
        return {
          target: getTarget(),
          options: {
            childList: true
          }
        };
      }
    })]
  });
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init() {
        return {};
      },
      // @ts-ignore - Workaround for help-center local consumption

      apply: function apply(_tr, pluginState, oldState, newState) {
        if (!oldState.selection.empty && isSelectionWithoutTextContent(newState.selection)) {
          experience.abort({
            reason: ABORT_REASON.SELECTION_CLEARED
          });
        }
        if (shiftArrowKeyPressed && !newState.selection.eq(oldState.selection) && !isSelectionWithoutTextContent(newState.selection)) {
          experience.start({
            method: START_METHOD.KEY_DOWN
          });
          shiftArrowKeyPressed = false;
        }
        return pluginState;
      }
    },
    props: {
      // @ts-ignore - Workaround for help-center local consumption

      handleDOMEvents: {
        mousedown: function mousedown(_view, e) {
          mouseDownPos = {
            x: e.clientX,
            y: e.clientY
          };
        },
        mouseup: function mouseup(view, e) {
          if (!mouseDownPos || isSelectionWithoutTextContent(view.state.selection) || isSelectionWithinCodeBlock(view.state.selection) || isSelectionToolbarWithinNode(getTarget())) {
            return;
          }
          if (e.clientX !== mouseDownPos.x || e.clientY !== mouseDownPos.y) {
            experience.start({
              method: START_METHOD.MOUSE_UP
            });
          }
        },
        dblclick: function dblclick(view, e) {
          if (isSelectionWithoutTextContent(view.state.selection) || isSelectionWithinCodeBlock(view.state.selection) || isSelectionToolbarWithinNode(getTarget())) {
            return;
          }
          experience.start({
            method: START_METHOD.MOUSE_UP
          });
        },
        keydown: function keydown(_view, _ref3) {
          var shiftKey = _ref3.shiftKey,
            key = _ref3.key;
          shiftArrowKeyPressed = shiftKey && key.includes('Arrow') && !isSelectionToolbarWithinNode(getTarget());
        },
        keyup: function keyup() {
          shiftArrowKeyPressed = false;
        }
      }
    },
    view: function view(_view2) {
      editorView = _view2;
      return {
        destroy: function destroy() {
          experience.abort({
            reason: ABORT_REASON.EDITOR_DESTROYED
          });
        }
      };
    }
  });
};
var isSelectionToolbarAddedInMutation = function isSelectionToolbarAddedInMutation(_ref4) {
  var type = _ref4.type,
    addedNodes = _ref4.addedNodes;
  // @ts-ignore - Workaround for help-center local consumption

  return type === 'childList' && _toConsumableArray(addedNodes).some(isSelectionToolbarWithinNode);
};
var isSelectionToolbarWithinNode = function isSelectionToolbarWithinNode(node) {
  return containsPopupWithNestedElement(node, '[data-testid="editor-floating-toolbar"]');
};
var isBlockMenuWithinNode = function isBlockMenuWithinNode(node) {
  return containsPopupWithNestedElement(node, '[data-testid="editor-block-menu"]');
};
var isSelectionWithoutTextContent = function isSelectionWithoutTextContent(selection) {
  if (!selection || selection.empty) {
    return true;
  }
  var hasText = false;
  selection.$from.doc.nodesBetween(selection.from, selection.to, function (node) {
    if (hasText) {
      return false;
    }
    if (node.isText && node.text && node.text.length > 0) {
      hasText = true;
      return false;
    }
    return true;
  });
  return !hasText;
};
var isSelectionWithinCodeBlock = function isSelectionWithinCodeBlock(selection) {
  var $from = selection.$from,
    $to = selection.$to;
  return $from.sameParent($to) && $from.parent.type.name === 'codeBlock';
};